import { useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"

import useGa4Property from "../hooks/useGa4Property"
import { supabase } from "../supabaseClient"

const MATCH_SOURCE_LABELS = {
  "hostname-report": "Hostname events",
  "data-stream-default-uri": "Data stream default URI",
}

const normalizeSourceLabel = (value) => {
  if (typeof value !== "string") return ""
  const trimmed = value.trim().toLowerCase()
  return trimmed
}

const formatMatchSources = (sources) => {
  if (!Array.isArray(sources) || sources.length === 0) return "—"
  const unique = Array.from(
    new Set(
      sources
        .map((value) => normalizeSourceLabel(value))
        .filter((value) => Boolean(value)),
    ),
  )
  if (!unique.length) return "—"
  return unique
    .map((value) => MATCH_SOURCE_LABELS[value] || value)
    .join(", ")
}

export default function GA4SelfReferralAudit() {
  const navigate = useNavigate()
  const { search } = useLocation()
  const propertyId = new URLSearchParams(search).get("id")
  const [rows, setRows] = useState([])
  const [auditLoading, setAuditLoading] = useState(false)
  const [auditError, setAuditError] = useState(null)
  const {
    property: propInfo,
    loading: propertyLoading,
    error: propertyError,
  } = useGa4Property(propertyId)

  const loading = propertyLoading || auditLoading

  useEffect(() => {
    if (propertyLoading) return

    if (propertyError || !propInfo?.id) {
      setRows([])
      setAuditError(propertyError || null)
      setAuditLoading(false)
      return
    }

    let cancelled = false

    const loadAudit = async () => {
      setAuditLoading(true)
      setAuditError(null)
      try {
        const { data: resultRows, error } = await supabase
          .from("ga4_self_referral_audit_results")
          .select("source, medium, session_count, matched_hostname, match_sources")
          .eq("ga4_property_id", propInfo.id)
          .order("session_count", { ascending: false })
        if (error) throw error
        if (cancelled) return
        setRows(Array.isArray(resultRows) ? resultRows : [])
      } catch (err) {
        if (cancelled) return
        console.error("Failed to load GA4 self referral audit", err)
        setRows([])
        setAuditError(err)
      } finally {
        if (!cancelled) setAuditLoading(false)
      }
    }

    loadAudit()

    return () => {
      cancelled = true
    }
  }, [propInfo?.id, propertyLoading, propertyError])

  const formattedRows = useMemo(
    () =>
      (Array.isArray(rows) ? rows : []).map((row) => ({
        source:
          typeof row?.source === "string" && row.source.trim()
            ? row.source.trim()
            : "(not set)",
        medium:
          typeof row?.medium === "string" && row.medium.trim()
            ? row.medium.trim()
            : "(not set)",
        sessionCount:
          typeof row?.session_count === "number"
            ? row.session_count
            : Number.isFinite(Number(row?.session_count))
            ? Number(row.session_count)
            : 0,
        matchedHostname:
          typeof row?.matched_hostname === "string" && row.matched_hostname.trim()
            ? row.matched_hostname.trim()
            : "—",
        matchSources: formatMatchSources(row?.match_sources),
      })),
    [rows],
  )

  return (
    <div className="page-container">
      <h2>Self Referral Audit</h2>
      <button onClick={() => navigate(`/ga4-property?id=${encodeURIComponent(propertyId)}`)}>
        Back
      </button>
      {loading ? (
        <p>Loading...</p>
      ) : propertyError ? (
        <p style={{ marginTop: "1rem", color: "#b00020" }}>
          Failed to load property details.
        </p>
      ) : auditError ? (
        <p style={{ marginTop: "1rem", color: "#b00020" }}>
          Failed to load self referral audit data.
        </p>
      ) : (
        <>
          {propInfo ? (
            <div style={{ marginTop: "1rem" }}>
              <h3 style={{ margin: 0 }}>
                {propInfo.property_name}
                <span
                  style={{
                    fontWeight: "normal",
                    fontSize: "0.9em",
                    marginLeft: "0.5rem",
                  }}
                >
                  ({propInfo.property_id})
                </span>
              </h3>
            </div>
          ) : (
            <p style={{ marginTop: "1rem" }}>Property not found.</p>
          )}
          {formattedRows.length ? (
            <table className="ui-table"
              border="1"
              cellPadding="8"
              style={{ width: "100%", marginTop: "1rem" }}
            >
              <thead>
                <tr>
                  <th>Source</th>
                  <th>Medium</th>
                  <th>Sessions</th>
                  <th>Matched Hostname</th>
                  <th>Match Sources</th>
                </tr>
              </thead>
              <tbody>
                {formattedRows.map((row, idx) => (
                  <tr key={idx}>
                    <td>{row.source}</td>
                    <td>{row.medium}</td>
                    <td>{row.sessionCount}</td>
                    <td>{row.matchedHostname}</td>
                    <td>{row.matchSources}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p style={{ marginTop: "1rem" }}>No results</p>
          )}
        </>
      )}
    </div>
  )
}
